/* ═══════════════════════════════════════════════════════════════════════════
   CUST-3.4 — MANUAL SEGMENT REFRESH · CONTROL PLANE
   ---------------------------------------------------------------------------
   Client-scoped sync status for the "Refresh Segments" button: single-flight
   locking, cooldown, max-duration (stuck-lock) guard, failure handling, and the
   shared in-progress state every viewer of the client converges on.

   Backing store: localStorage keyed by client id. That choice is deliberate —
   it is *shared* rather than per-component/per-session, so two tabs of the same
   client genuinely observe one another's refresh (the browser fires `storage`
   in the other tabs), which is the closest faithful stand-in for the shared
   `segment_sync_status` record described in Appendix E. Swapping this module
   for the real endpoint later means replacing readStatus/writeStatus/runRefresh
   with API calls — the hook and UI contract stay as they are.

   Scope (per the story): this gates ONLY the segmentation-derived views —
   Segment Breakdown (CUST-3.3), Avg Spend by Segment (CUST-12), the RFM Detail
   panel (CUST-17) and each customer's segment label. The six top KPI cards,
   Top Customers, Channel Performance, Complaints and Cancellations are computed
   from live aggregates and are never gated by this button.
   ═══════════════════════════════════════════════════════════════════════════ */

import { useCallback, useEffect, useRef, useState } from "react";

export type SyncState = "idle" | "in_progress" | "success" | "failed";

export interface SegmentSyncStatus {
    state: SyncState;
    startedAt: number | null;
    finishedAt: number | null;
    lastSuccessAt: number | null;
    triggeredBy: string | null;
}

/** Defaults from the story; all configurable in one place. */
export const SYNC_COOLDOWN_MS = 5 * 60 * 1000;      // confirm before re-refreshing
export const SYNC_MAX_DURATION_MS = 5 * 60 * 1000;  // force-release a stuck lock
export const SYNC_POLL_MS = 3000;                   // in-progress convergence poll
const FRESHNESS_TICK_MS = 30 * 1000;                // keeps "n minutes ago" honest
const REFRESH_DURATION_MS = 1600;                   // simulated Layer-2 snapshot

const KEY = (clientId: string) => `maghil.customers.segmentSync.${clientId || "default"}`;

const IDLE: SegmentSyncStatus = { state: "idle", startedAt: null, finishedAt: null, lastSuccessAt: null, triggeredBy: null };

/* ─── Local subscriber registry (same-tab); `storage` covers other tabs ────── */
type Listener = (s: SegmentSyncStatus) => void;
const listeners = new Map<string, Set<Listener>>();

function emit(clientId: string, status: SegmentSyncStatus) {
    listeners.get(clientId)?.forEach((fn) => fn(status));
}

/** Read status, applying the stuck-lock guard so a crashed refresh self-heals. */
export function readStatus(clientId: string): SegmentSyncStatus {
    let raw: string | null = null;
    try { raw = localStorage.getItem(KEY(clientId)); } catch { /* storage unavailable */ }

    if (!raw) {
        // Mock-only seed: a client that has already computed segments some time
        // ago, so the report renders populated on a cold open. With a real API
        // this branch disappears and a never-computed client legitimately falls
        // through to the "Segments not yet computed" empty state below.
        return { ...IDLE, lastSuccessAt: Date.now() - 2 * 60 * 60 * 1000 };
    }

    let parsed: SegmentSyncStatus;
    try { parsed = { ...IDLE, ...JSON.parse(raw) }; } catch { return { ...IDLE }; }

    // Max-duration guard: a lock older than the guard is presumed dead. Report it
    // as failed rather than leaving the client permanently blocked.
    if (parsed.state === "in_progress" && parsed.startedAt && Date.now() - parsed.startedAt > SYNC_MAX_DURATION_MS) {
        return { ...parsed, state: "failed", finishedAt: Date.now() };
    }
    return parsed;
}

function writeStatus(clientId: string, status: SegmentSyncStatus) {
    try { localStorage.setItem(KEY(clientId), JSON.stringify(status)); } catch { /* ignore */ }
    emit(clientId, status);
}

export function subscribe(clientId: string, fn: Listener): () => void {
    if (!listeners.has(clientId)) listeners.set(clientId, new Set());
    listeners.get(clientId)!.add(fn);
    return () => { listeners.get(clientId)?.delete(fn); };
}

/** True when a successful refresh happened inside the cooldown window. */
export function withinCooldown(status: SegmentSyncStatus): boolean {
    return !!status.lastSuccessAt && Date.now() - status.lastSuccessAt < SYNC_COOLDOWN_MS;
}

export function minutesSinceSuccess(status: SegmentSyncStatus): number {
    if (!status.lastSuccessAt) return 0;
    return Math.max(0, Math.round((Date.now() - status.lastSuccessAt) / 60000));
}

export type RefreshOutcome =
    | { started: true }
    | { started: false; reason: "in_progress" }
    | { started: false; reason: "cooldown"; minutesAgo: number };

/**
 * Single-flight refresh. Returns without starting when another refresh already
 * holds the client lock, or when inside the cooldown window and not forced.
 * `simulateFailure` exercises the failure path (last-good data is retained).
 */
export function requestRefresh(
    clientId: string,
    triggeredBy: string,
    opts: { force?: boolean; simulateFailure?: boolean } = {},
): RefreshOutcome {
    const current = readStatus(clientId);

    if (current.state === "in_progress") return { started: false, reason: "in_progress" };
    if (!opts.force && withinCooldown(current)) {
        return { started: false, reason: "cooldown", minutesAgo: minutesSinceSuccess(current) };
    }

    writeStatus(clientId, { ...current, state: "in_progress", startedAt: Date.now(), finishedAt: null, triggeredBy });

    // Layer 2 in Appendix E: recompute this one client's quintile thresholds.
    // Nothing here scans raw orders — the real job reads the incremental
    // customer_stats aggregate, which is what keeps it seconds rather than minutes.
    window.setTimeout(() => {
        const running = readStatus(clientId);
        if (running.state !== "in_progress") return; // superseded or force-released
        if (opts.simulateFailure) {
            // Failure releases the lock and keeps the previous lastSuccessAt, so
            // the last-good segments stay on screen — a failed refresh never blanks.
            writeStatus(clientId, { ...running, state: "failed", finishedAt: Date.now() });
            return;
        }
        const now = Date.now();
        writeStatus(clientId, { ...running, state: "success", finishedAt: now, lastSuccessAt: now });
    }, REFRESH_DURATION_MS);

    return { started: true };
}

/** "just now" · "5 minutes ago" · "2 hours ago" · "12 Aug 2026". */
export function formatFreshness(ts: number | null): string {
    if (!ts) return "";
    const mins = Math.floor((Date.now() - ts) / 60000);
    if (mins < 1) return "just now";
    if (mins === 1) return "1 minute ago";
    if (mins < 60) return `${mins} minutes ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs === 1) return "1 hour ago";
    if (hrs < 24) return `${hrs} hours ago`;
    const days = Math.floor(hrs / 24);
    return days === 1 ? "1 day ago" : `${days} days ago`;
}

export const exactTimestamp = (ts: number | null): string =>
    ts ? new Date(ts).toLocaleString() : "";

/* ═══════════════════════════════════════════════════════════════════════════
   HOOK — status + refresh action for the UI
   ═══════════════════════════════════════════════════════════════════════════ */
export function useSegmentSync(clientId: string, triggeredBy: string) {
    const [status, setStatus] = useState<SegmentSyncStatus>(() => readStatus(clientId));
    const [, forceTick] = useState(0);
    const clientRef = useRef(clientId);
    clientRef.current = clientId;

    useEffect(() => {
        setStatus(readStatus(clientId));
        const onLocal = (s: SegmentSyncStatus) => setStatus(s);
        const unsub = subscribe(clientId, onLocal);
        // Another tab of the same client wrote the shared record.
        const onStorage = (e: StorageEvent) => {
            if (e.key === KEY(clientId)) setStatus(readStatus(clientId));
        };
        window.addEventListener("storage", onStorage);
        return () => { unsub(); window.removeEventListener("storage", onStorage); };
    }, [clientId]);

    // Poll while in progress so every viewer converges within a few seconds; the
    // same tick re-applies the stuck-lock guard. Slow ticker otherwise, only to
    // keep the relative "n minutes ago" label from going stale.
    useEffect(() => {
        const inProgress = status.state === "in_progress";
        const id = window.setInterval(() => {
            const next = readStatus(clientRef.current);
            setStatus((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
            forceTick((n) => n + 1);
        }, inProgress ? SYNC_POLL_MS : FRESHNESS_TICK_MS);
        return () => window.clearInterval(id);
    }, [status.state]);

    const refresh = useCallback(
        (opts: { force?: boolean } = {}) => requestRefresh(clientId, triggeredBy, opts),
        [clientId, triggeredBy],
    );

    return {
        status,
        refresh,
        refreshing: status.state === "in_progress",
        neverComputed: !status.lastSuccessAt,
        freshness: formatFreshness(status.lastSuccessAt),
        exact: exactTimestamp(status.lastSuccessAt),
    };
}
